import _ from 'lodash';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { captureException } from '@playbookux/playbook-web-core';
import PaymentInformation from '../../../../../core/components/payment-information';
import StepsModal from '../../../../../core/components/steps-modal';

import { UPDATE_PARTICIPANTS_NUMBER } from './graphql';
import {
  ContainerInput,
  ContainerText,
  ErrorMessage,
  Input,
  ModalContent,
} from './styles';

const UnmoderatedParticipantsModal = (props) => {
  const {
    onClose,
    onSubmit,
    open,
    project,
    refetchProject,
    segmentAudience,
  } = props;

  const {
    id: projectId,
    company: { id: companyId },
  } = project;

  const {
    id: segmentId,
    audienceParticipants,
  } = segmentAudience;

  const couponCode = null;

  const [activeIndex, setActiveIndex] = useState(0);
  const [tokenId, setTokenId] = useState(null);
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [rememberCard, setRememberCard] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);

  const participants = parseInt(value, 10);

  const [updateParticipantsNumber] = useMutation(UPDATE_PARTICIPANTS_NUMBER);

  const schema = yup
    .number()
    .integer('Number of participants must be an integer')
    .positive('Number of participants must be a positive number')
    .typeError('Number of participants must be a positive number');

  useEffect(() => {
    if ((tokenId || paymentMethodId) && activeIndex) {
      setPaymentError(null);
    }
  }, [tokenId, paymentMethodId, activeIndex]);

  const handleUpdateParticipantsNumber = async () => {
    setError(null);
    setPaymentError(null);
    setIsLoading(true);

    try {
      await updateParticipantsNumber({
        variables: {
          projectId,
          participantsNumber: audienceParticipants + participants,
          token: tokenId,
          couponCode,
          rememberCard,
          paymentMethodId,
          segmentId,
        },
      });
      await refetchProject();
      onSubmit();
      setValue(null);
      setActiveIndex(0);
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      const message = graphQLError ? graphQLError.message : 'Error while charging your card.';
      captureException('UPDATE_PARTICIPANTS_NUMBER_FAILED', err);
      setPaymentError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = () => {
    if (error || value <= 0) {
      setError('Invalid number of participants.');
    } else {
      setActiveIndex(1);
    }
  };

  const handleNewFieldValues = (string, stateValue) => {
    if (stateValue === rememberCard || stateValue === paymentMethodId) return;

    if (string === 'rememberCard') {
      setRememberCard(stateValue);
    }

    if (string === 'paymentMethodId') {
      setPaymentMethodId(stateValue);
      setTokenId(null);
    }
  };

  const handleCallback = (id) => {
    setTokenId(id);
    setPaymentMethodId(null);
  };

  const handleChange = (e, { value: newValue }) => {
    setValue(newValue);
    setError();
    schema.validate(newValue)
      .catch((err) => {
        setError(err.message);
      });
  };

  const handleCloseModal = () => {
    setError();
    onClose();
    setValue('');
  };

  return (
    <StepsModal
      activeIndex={activeIndex}
      open={open}
      onClose={handleCloseModal}
      components={[
        {
          buttonText: 'Checkout',
          onClick: () => handleCheckout(),
          component: (
            <>
              <ModalContent>
                <ContainerText>
                  How many more participants should we recruit?
                </ContainerText>
                <ContainerInput>
                  <Input
                    onChange={handleChange}
                    value={value}
                    error={!!error}
                  />
                </ContainerInput>
              </ModalContent>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </>
          ),
        },
        {
          onClickBack: () => setActiveIndex(0),
          buttonText: 'Submit & Pay',
          onClick: () => !isLoading && handleUpdateParticipantsNumber(),
          loading: isLoading,
          component: (
            <>
              <PaymentInformation
                projectId={projectId}
                companyId={companyId}
                couponCode={couponCode}
                participants={participants}
                callback={handleCallback}
                onSetNewValues={handleNewFieldValues}
              />
              {paymentError && <ErrorMessage>{paymentError}</ErrorMessage>}
            </>
          ),
        },
      ]}
    />
  );
};

UnmoderatedParticipantsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    company: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  segmentAudience: PropTypes.shape({
    id: PropTypes.string,
    audienceParticipants: PropTypes.number,
  }).isRequired,
  refetchProject: PropTypes.func.isRequired,
};

UnmoderatedParticipantsModal.defaultProps = {
  open: false,
};

export default UnmoderatedParticipantsModal;
